import { useMemo } from 'react';
import { LayoutGrid, List } from 'lucide-react';
import type { Question } from '../types';

interface PreviewControlsProps {
  question: Question;
  examQuestions: Question[];
  onExamQuestionsChange: (questions: Question[] | ((prev: Question[]) => Question[])) => void;
  onHasWidthChangesChange: (hasChanges: boolean) => void;
}

// 快捷缩放按钮值
const QUICK_SCALES = [20, 40, 60, 80, 99, 120, 140, 160, 180, 200];

export default function PreviewControls({
  question,
  examQuestions,
  onExamQuestionsChange,
  onHasWidthChangesChange
}: PreviewControlsProps) {
  const hasOptionImages = question?.options?.some(
    o => o.image || (o.images && o.images.length > 0)
  );

  if (!hasOptionImages ||
      (question.type !== 'single_choice' &&
       question.type !== 'multi_choice' &&
       question.type !== 'judge')) {
    return null;
  }

  const currentUniformScale = useMemo(() => {
    if (!question?.options) return 100;
    const optionsWithImages = question.options.filter(
      o => o.image || (o.images && o.images.length > 0)
    );
    if (optionsWithImages.length === 0) return 100;

    const allWidths: number[] = [];
    optionsWithImages.forEach(o => {
      if (o.imageWidth != null) allWidths.push(o.imageWidth);
      if (o.imageWidths && o.imageWidths.length > 0) {
        allWidths.push(...o.imageWidths);
      }
    });

    if (allWidths.length === 0) return 100;

    const firstWidth = allWidths[0];
    if (allWidths.every(w => w === firstWidth)) return firstWidth;

    return Math.round(allWidths.reduce((sum, w) => sum + w, 0) / allWidths.length);
  }, [question?.options]);

  const handleOptionLayoutChange = (layout: 'grid' | 'list') => {
    // 关键修复：使用函数式更新，避免闭包陷阱导致覆盖其他修改（如题干图片）
    onExamQuestionsChange((prevQuestions: Question[]) =>
      prevQuestions.map(q =>
        q.id === question.id ? { ...q, optionLayout: layout } : q
      )
    );
    onHasWidthChangesChange(true);
  };

  const handleUniformScaleChange = (scale: number) => {
    // 关键修复：使用函数式更新，避免闭包陷阱导致覆盖其他修改（如题干图片）
    onExamQuestionsChange((prevQuestions: Question[]) =>
      prevQuestions.map(q => {
        if (q.id !== question.id) return q;

        const newOptions = q.options.map(o => {
          const hasImage = o.image || (o.images && o.images.length > 0);
          if (!hasImage) return o;

          return {
            ...o,
            imageWidth: o.image ? scale : o.imageWidth,
            imageWidths: o.images ? o.images.map(() => scale) : o.imageWidths,
          };
        });

        return { ...q, options: newOptions };
      })
    );
    onHasWidthChangesChange(true);
  };

  return (
    <div className="mb-3 flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-200">
      {/* 布局切换按钮 */}
      <div className="flex items-center gap-0.5 shrink-0">
        <button
          onClick={() => handleOptionLayoutChange('grid')}
          className={`p-1 rounded transition-colors ${
            (question.optionLayout || 'grid') === 'grid'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          title="1行2选项"
        >
          <LayoutGrid className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => handleOptionLayoutChange('list')}
          className={`p-1 rounded transition-colors ${
            question.optionLayout === 'list'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          title="1行1选项"
        >
          <List className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* 分隔线 */}
      <div className="w-px h-4 bg-gray-300 shrink-0" />

      {/* 快捷缩放按钮 - 精简为5档 */}
      <div className="flex items-center gap-1 shrink-0">
        {QUICK_SCALES.map(scale => (
          <button
            key={scale}
            onClick={() => handleUniformScaleChange(scale)}
            className={`px-2 py-0.5 rounded text-xs font-medium transition-colors min-w-[36px] ${
              currentUniformScale === scale
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {scale}
          </button>
        ))}
      </div>

      {/* 微调滑块 */}
      <div className="flex items-center gap-1.5 flex-1 min-w-[120px] max-w-[200px]">
        <input
          type="range"
          min="10"
          max="200"
          value={currentUniformScale}
          onChange={(e) => handleUniformScaleChange(parseInt(e.target.value))}
          className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
        />
        <span className="text-xs text-gray-500 w-9 text-right shrink-0 tabular-nums">
          {currentUniformScale}%
        </span>
      </div>
    </div>
  );
}
